import jwtDecode from 'jwt-decode';
import axios, {
  axiosEtrack1Instance,
  axiosAiInstance,
  axiosDwInstance,
  axiosAmApiInstance
} from './axios';
import { getProvisionalToken, setProvisionalToken } from './oidcTokenService';

const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }
  const decoded = jwtDecode<{ exp: number }>(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const getUserRole = (accessToken: string) => {
  if (!accessToken) {
    return undefined;
  }
  const decoded = jwtDecode<{ role: string }>(accessToken);
  return decoded.role;
};

const getUserId = () => {
  const accessToken = localStorage.getItem('accessToken') ?? getProvisionalToken();
  if (!accessToken) {
    return undefined;
  }
  const decoded = jwtDecode<{ nameid: string }>(accessToken);
  return decoded.nameid;
};

const getApplicationClaims = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode<{
    Applications: string[];
    features: string[];
    management: string[];
    actions: string[];
    permissions: string[];
  }>(accessToken);

  //if there is only one claim, the value is a string, instead of an array of strings
  const getClaimArray = (claimType: string, claims?: string[] | string) => {
    let claimArray: string[];
    if (typeof claims === 'string') {
      claimArray = [claims];
    } else if (claims) {
      claimArray = claims;
    } else {
      claimArray = [];
    }
    claimArray = claimArray.map((claim) => `${claimType}_${claim.toLowerCase()}`);
    return claimArray;
  };
  const Applications = getClaimArray('applications', decoded?.Applications);
  const features = getClaimArray('features', decoded?.features);
  const management = getClaimArray('management', decoded?.management);
  const actions = getClaimArray('actions', decoded?.actions);
  const permissions = getClaimArray('permissions', decoded?.permissions);

  //create one array with claims from both Applications, features, and management
  const allClaims = [...Applications, ...features, ...management, ...actions, ...permissions];
  //remove any duplicates
  const allClaimsNoDublicates = Array.from(new Set(allClaims));
  return allClaimsNoDublicates;
};

const setAuthorizationHeaders = (token: string) => {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  axiosEtrack1Instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  axiosAiInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  axiosDwInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  axiosAmApiInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
};

const setProvisionalSession = (provisionalToken: string) => {
  localStorage.removeItem('accessToken');
  setProvisionalToken(provisionalToken);
  setAuthorizationHeaders(provisionalToken);
};

const setSession = (accessToken: string | null) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    setAuthorizationHeaders(accessToken);
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
    delete axiosEtrack1Instance.defaults.headers.common.Authorization;
    delete axiosAiInstance.defaults.headers.common.Authorization;
    delete axiosDwInstance.defaults.headers.common.Authorization;
    delete axiosAmApiInstance.defaults.headers.common.Authorization;
  }
};

/**
 * Extracts the scope claim from a JWT token.
 * OpenIddict tokens use 'oi_scp' for scope claims.
 */
const getTokenScope = (accessToken: string): string[] => {
  if (!accessToken) return [];
  try {
    const decoded = jwtDecode<{ scope?: string | string[]; oi_scp?: string | string[] }>(
      accessToken
    );
    const scopeClaim = decoded.oi_scp ?? decoded.scope;
    if (!scopeClaim) return [];
    return typeof scopeClaim === 'string' ? scopeClaim.split(' ') : scopeClaim;
  } catch {
    return [];
  }
};

export {
  isValidToken,
  setSession,
  setProvisionalSession,
  getApplicationClaims,
  getUserRole,
  getUserId,
  getTokenScope
};
